/**
 * 路由權限映射
 * 
 * 定義路由路徑與權限模組的對應關係
 * 確保 Navigation 與 Route Access 完全一致
 * 
 * @module RoutePermissions
 */

import type { AdminRole } from './roles';
import type { Module } from './permissions';
import { hasPermission } from './permissions';

/**
 * 路由權限映射表
 * 
 * 格式：{ 路徑: { module, action } }
 */
export const routePermissionMap: Record<string, { module: Module; action: 'view_list' | 'view_detail' }> = {
  // ==================== 基礎頁面 ====================
  '/admin/dashboard': { module: 'dashboard', action: 'view_list' },
  
  // ==================== 訂單管理 ====================
  '/admin/orders': { module: 'orders', action: 'view_list' },
  '/admin/orders/:id': { module: 'orders', action: 'view_detail' },
  '/admin/orders/contract': { module: 'orders', action: 'view_detail' },
  
  // ==================== 客戶管理 ====================
  '/admin/customers': { module: 'customers', action: 'view_list' },
  
  // ==================== 器材管理 ====================
  '/admin/equipment': { module: 'equipment', action: 'view_list' },
  '/admin/equipment/items': { module: 'equipment_items', action: 'view_list' },
  '/admin/categories': { module: 'categories', action: 'view_list' },
  '/admin/brands': { module: 'brands', action: 'view_list' },
  '/admin/accessories': { module: 'accessories', action: 'view_list' },
  
  // ==================== 據點管理 ====================
  '/admin/locations': { module: 'locations', action: 'view_list' },
  
  // ==================== 車輛管理 ====================
  '/admin/vehicles': { module: 'vehicles', action: 'view_list' },
  
  // ==================== Urent 管理 ====================
  '/admin/urent': { module: 'urent', action: 'view_list' },
  '/admin/urent/ledger': { module: 'urent_ledger', action: 'view_list' },
  '/admin/urent-contracts': { module: 'urent_contracts', action: 'view_list' },
  
  // ==================== 儲值管理 ====================
  '/admin/prepaid': { module: 'prepaid', action: 'view_list' },
  '/admin/prepaid/customers': { module: 'prepaid_customers', action: 'view_list' },
  '/admin/prepaid/ledger': { module: 'prepaid_ledger', action: 'view_list' },
  '/admin/prepaid/plans': { module: 'prepaid_plans', action: 'view_list' },
  
  // ==================== 業績報表 ====================
  '/admin/analytics': { module: 'analytics', action: 'view_list' },

  // ==================== 出勤管理 ====================
  '/admin/attendance': { module: 'attendance', action: 'view_list' },
  
  // ==================== 合約管理（法務文件）====================
  // 僅 owner + super_admin 可進入
  '/admin/contracts': { module: 'contracts', action: 'view_list' },
  
  // ==================== 表單管理 ====================
  '/admin/forms': { module: 'forms', action: 'view_list' },
  
  // ==================== CMS ====================
  '/admin/cms': { module: 'cms', action: 'view_list' },
  
  // ==================== 前台圖片管理 ====================
  '/admin/frontend-images': { module: 'frontend_images', action: 'view_list' },

  // ==================== 甲方資料設定 ====================
  '/admin/legal-settings': { module: 'legal_settings', action: 'view_list' },

  // ==================== 里程碑管理 ====================
  '/admin/milestones': { module: 'milestones', action: 'view_list' },

  // ==================== 案例影片管理 ====================
  '/admin/service-cases': { module: 'service_cases', action: 'view_list' },

  // ==================== MuVi Space 管理 ====================
  '/admin/muvi-space/bookings': { module: 'muvi_bookings', action: 'view_list' },
  '/admin/muvi-space/content': { module: 'muvi_content', action: 'view_list' },
  '/admin/muvi-space/partners': { module: 'muvi_partners', action: 'view_list' },

  // ==================== AI Agent 管理 ====================
  '/admin/ai-agent': { module: 'ai_agent', action: 'view_list' },

  // ==================== 排班管理 ====================
  '/admin/schedules': { module: 'schedules', action: 'view_list' },

  // ==================== 管理員管理（系統治理）====================
  // 僅 owner 可進入
  '/admin/admins': { module: 'admins', action: 'view_list' },
  
  // ==================== 系統設定（系統治理）====================
  // 僅 owner 可進入
  '/admin/settings': { module: 'settings', action: 'view_list' },
  '/admin/settings/google-calendar': { module: 'google_calendar', action: 'view_list' },
  '/admin/settings/google-calendar/callback': { module: 'google_calendar', action: 'view_detail' },
  '/admin/settings/line-notify': { module: 'line_notify', action: 'view_list' },
  '/admin/settings/api': { module: 'api_settings', action: 'view_list' },
  '/admin/settings/storefront-sort': { module: 'storefront_sort', action: 'view_list' },
  
  // ==================== 系統治理資訊（owner only）====================
  '/admin/system-governance': { module: 'system_governance', action: 'view_list' },

  // ==================== 測試頁面 ====================
  '/admin/calendar-test': { module: 'settings', action: 'view_list' },
};

/**
 * 檢查角色是否可以訪問特定路由
 * 
 * @param role - 角色
 * @param path - 路徑
 * @returns true 如果可以訪問
 */
export function canAccessRoute(role: AdminRole, path: string): boolean {
  // 移除尾部斜線
  const normalizedPath = path.replace(/\/$/, '');
  
  // 精確匹配
  const exactMatch = routePermissionMap[normalizedPath];
  if (exactMatch) {
    return hasPermission(role, exactMatch.module, exactMatch.action);
  }
  
  // 動態路由匹配（例如 /admin/orders/123 匹配 /admin/orders/:id）
  for (const [routePath, permission] of Object.entries(routePermissionMap)) {
    if (routePath.includes(':')) {
      const pattern = routePath.replace(/:[^/]+/g, '[^/]+');
      const regex = new RegExp(`^${pattern}$`);
      if (regex.test(normalizedPath)) {
        return hasPermission(role, permission.module, permission.action);
      }
    }
  }
  
  // 預設：如果找不到對應的權限設定，拒絕訪問
  return false;
}

/**
 * 取得路由對應的模組
 * 
 * @param path - 路徑
 * @returns 模組名稱，如果找不到則返回 null
 */
export function getRouteModule(path: string): Module | null {
  const normalizedPath = path.replace(/\/$/, '');
  const exactMatch = routePermissionMap[normalizedPath];
  
  if (exactMatch) {
    return exactMatch.module;
  }
  
  // 動態路由匹配
  for (const [routePath, permission] of Object.entries(routePermissionMap)) {
    if (routePath.includes(':')) {
      const pattern = routePath.replace(/:[^/]+/g, '[^/]+');
      const regex = new RegExp(`^${pattern}$`);
      if (regex.test(normalizedPath)) {
        return permission.module;
      }
    }
  }
  
  return null;
}
